import * as React from "react";
import { FormattedMessage, FormattedNumber } from "react-intl";
import { Link } from "react-router-dom";
import { FollowButton } from "common/components/followButton";
import { IconWrapper } from "common/icons";
import PACKS from "common/icons/packs";
import CommunityChecker from "@vingle/wtf-sdk/components/communityChecker";
import { VingleURL } from "@vingle/wtf-sdk/url";
import SimpleFollowButton from "common/components/interestHeader/components/simpleFollowButton";
import { useTrackTicket } from "@vingle/wtf-sdk/helpers/trackTicket";
import { isBrowser } from "@vingle/wtf-sdk/helpers/envChecker";

const styles = require("./interestList.scss");

interface IProps {
  interest: Vingle.Interest.IInterest;
  isLast: boolean;
  onFollowClick: (interest: Vingle.Interest.IInterest) => void;
  isNotCreatedInterest: boolean;
}

const InterestInfo = React.memo(
  ({
    followersCount,
    cardCount,
    language,
    isFollowing,
    isNotCreatedInterest,
  }: {
    followersCount?: number;
    cardCount?: number;
    language?: string;
    isFollowing?: boolean;
    isNotCreatedInterest: boolean;
  }) => {
    let followerCountDisplay: React.ReactNode = null;
    let cardCountDisplay: React.ReactNode = null;
    let firstFollowerCTA: React.ReactNode = null;

    if (isNotCreatedInterest) {
      firstFollowerCTA = (
        <FormattedMessage
          id={
            isFollowing
              ? "search_page.interest.first_follower"
              : "search_page.interest.first_interest"
          }
        />
      );
    } else {
      if (followersCount || followersCount === 0) {
        followerCountDisplay = (
          <FormattedMessage
            id="search_page.interest.followers_count"
            values={{ count: <FormattedNumber value={followersCount} /> }}
          />
        );
      }
      if (cardCount || cardCount === 0) {
        cardCountDisplay = (
          <FormattedMessage
            id="search_page.interest.card_count"
            values={{ count: <FormattedNumber value={cardCount} /> }}
          />
        );
      }
    }

    return (
      <div className={styles.info}>
        {language ? (
          <React.Fragment>
            <span className={styles.language}>{language}</span>
            <div className={styles.sep} />
          </React.Fragment>
        ) : null}
        {followerCountDisplay}
        {cardCountDisplay}
        {firstFollowerCTA}
      </div>
    );
  },
);

const InterestItem = React.memo((props: IProps) => {
  const { interest, isLast, isNotCreatedInterest, onFollowClick } = props;
  const handleFollowClick = React.useCallback(() => {
    onFollowClick(interest);
  }, [interest]);
  const { handleRead } = useTrackTicket({
    contentType: "interest",
    contentId: interest.name,
    referralUrl: `${new VingleURL.SearchInterest().toSchema()}${
      isBrowser() ? location.search : ""
    }`,
  });
  const interestTitleElement = (
    <React.Fragment>
      <CommunityChecker
        interest={interest}
        checkType="IS_COMMUNITY"
        renderWhenCheckFalse={
          <IconWrapper
            icon={PACKS.INTEREST_MARK.SEARCH.INTEREST_MARK}
            className={styles.interestMark}
          />
        }
      >
        <IconWrapper
          icon={PACKS.INTEREST_MARK.SEARCH.COMMUNITY_MARK}
          className={styles.interestMark}
        />
      </CommunityChecker>
      <div className={styles.title} title={interest.name.split(":")[0]}>
        {interest.name.split(":")[0]}
      </div>
    </React.Fragment>
  );
  return (
    <div className={`${styles.item} ${isLast ? styles.noBottomLine : ""}`}>
      <div className={styles.left}>
        {isNotCreatedInterest ? (
          <div
            className={styles.titleWrapper}
            title={interest.name.split(":")[0]}
          >
            {interestTitleElement}
          </div>
        ) : (
          <Link
            to={new VingleURL.InterestShow({
              interest: interest.name,
            }).toString()}
            className={styles.titleWrapper}
            onClick={handleRead}
          >
            {interestTitleElement}
          </Link>
        )}
        <InterestInfo
          language={interest.language}
          followersCount={interest.followers_count}
          cardCount={interest.card_count}
          isFollowing={interest.relation ? interest.relation.following : false}
          isNotCreatedInterest={isNotCreatedInterest}
        />
      </div>
      <div className={styles.right}>
        {isNotCreatedInterest ? (
          <SimpleFollowButton
            forCommunity={false}
            isFollowing={
              interest.relation ? interest.relation.following : false
            }
            onFollowClick={handleFollowClick}
          />
        ) : (
          <FollowButton
            isFollowing={
              interest.relation ? interest.relation.following : false
            }
            onFollowClick={handleFollowClick}
          />
        )}
      </div>
    </div>
  );
});
InterestItem.displayName = "InterestItem";

interface IInterestsListProps {
  interests: ReadonlyArray<Vingle.Search.ISearchInterest>;
  isNotCreatedInterest: boolean;
  onFollowClick: (interest: Vingle.Interest.IInterest) => void;
}

const InterestList = ({
  interests,
  onFollowClick,
  isNotCreatedInterest,
}: IInterestsListProps) => {
  const items = interests.map((interest, idx) => {
    if (interest) {
      return (
        <InterestItem
          key={`${interest.interest.name}_${idx}`}
          interest={interest.interest}
          isLast={idx === interests.length - 1}
          onFollowClick={onFollowClick}
          isNotCreatedInterest={isNotCreatedInterest}
        />
      );
    }
    return null;
  });
  return <div className={styles.container}>{items}</div>;
};

export default InterestList;
